import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  Button,
  TextField,
  DialogActions,
  DialogContent,
  InputAdornment,
  Box,
  ListItemButton,
  ListItemAvatar,
  Checkbox,
  ListItem,
  List,
  ListItemText,
  ListItemIcon,
  DialogTitle,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAllUsersForAddUserDialogQuery, FilterForUsers } from '@/__generated__/graphql';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { containedButtonStyle, textButtonStyle } from '@/theme';

type Props = {
  open: boolean;
  handleToggle: () => void;
  handleCheckedUsers: Function;
  handleAddUsers: () => void;
  checked: Array<string>;
  queryVariable: FilterForUsers;
};

const filterUsersVariables: FilterForUsers = {
  connection: undefined,
  roleId: undefined,
  q: undefined,
};

export const AddUserDialog = ({
  open,
  handleToggle,
  handleAddUsers,
  handleCheckedUsers,
  checked,
  queryVariable,
}: Props) => {
  const [searchTerms, setSearchTerms] = useState('');
  const [unfinishedSearchTerm, setUnfinishedSearchTerm] = useState('');

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerms(unfinishedSearchTerm);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [setSearchTerms, unfinishedSearchTerm]);

  const { data, refetch } = useAllUsersForAddUserDialogQuery({
    variables: {
      input: {
        ...queryVariable,
      },
    },
  });

  const filterUserOptions = useCallback(async () => {
    if (searchTerms) {
      filterUsersVariables.q = searchTerms;
    } else {
      filterUsersVariables.q = undefined;
    }
    refetch({
      input: {
        ...queryVariable,
        ...filterUsersVariables,
      },
    });
  }, [searchTerms, refetch, queryVariable]);

  useEffect(() => {
    filterUserOptions();
  }, [filterUserOptions, searchTerms]);

  const handleSearchTermChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUnfinishedSearchTerm(event.target.value);
    },
    [setUnfinishedSearchTerm]
  );

  const handleCloseDialog = useCallback(() => {
    handleToggle();
    setUnfinishedSearchTerm('');
  }, [handleToggle]);

  const handleAddMembers = useCallback(() => {
    handleAddUsers();
    setUnfinishedSearchTerm('');
  }, [handleAddUsers]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      PaperProps={{
        sx: {
          minWidth: '50rem',
        },
      }}
    >
      <DialogTitle>Add Members</DialogTitle>
      <DialogContent>
        <TextField
          data-testid="search-user-field"
          id="standard-basic"
          label="Search user"
          variant="standard"
          type="search"
          value={unfinishedSearchTerm}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={handleSearchTermChanged}
        />
      </DialogContent>
      <Box>
        <List
          sx={{
            height: 345,
            minHeight: 345,
            overflowY: 'auto',
          }}
        >
          {data &&
            data.allUsers?.map((user) => (
              <ListItem
                key={user.id}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRight: 0,
                  borderLeft: 0,
                  '&:hover': { backgroundColor: '#6582a40d' },
                }}
              >
                <ListItemButton
                  onClick={handleCheckedUsers(user.id)}
                  sx={{ '&:hover': { background: 'none' } }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox edge="start" checked={checked.includes(user.id)} disableRipple />
                  </ListItemIcon>
                  <ListItemAvatar sx={{ pr: 0 }}>
                    <UserAvatar
                      name={user?.name}
                      picture={user?.picture}
                      sx={{ width: 30, height: 30 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.4rem', fontWeight: '600' }}
                    id={user.id}
                    primary={user?.name}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Box>

      <Divider orientation="horizontal" />

      <DialogActions sx={{ pt: 2, pb: 2 }}>
        <Button onClick={handleCloseDialog} variant="text" sx={textButtonStyle}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={containedButtonStyle}
          onClick={handleAddMembers}
          disabled={checked.length === 0}
          data-testid="add-member-button"
        >
          Add members
        </Button>
      </DialogActions>
    </Dialog>
  );
};
